import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import TicketsManager from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const defaultTicketsManagerWidth = 550;
const minTicketsManagerWidth = 404;
const maxTicketsManagerWidth = 700;

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    flex: 1,
    padding: theme.spacing(1.25),
    height: `calc(100% - 48px)`,
    overflowY: "hidden",
    background:
      theme.mode === "light"
        ? "radial-gradient(circle at top left, rgba(39, 124, 255, 0.10), transparent 34%), linear-gradient(180deg, #eef5fb 0%, #e4edf7 100%)"
        : "radial-gradient(circle at top left, rgba(48, 172, 255, 0.12), transparent 28%), linear-gradient(180deg, #122030 0%, #0f1c29 100%)",
  },
  chatPaper: {
    display: "flex",
    height: "100%",
    gap: theme.spacing(1.25),
  },
  contactsWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflowY: "hidden",
    position: "relative",
    borderRadius: 24,
    background: theme.mode === "light" ? "rgba(255,255,255,0.82)" : "rgba(17, 30, 45, 0.86)",
    boxShadow:
      theme.mode === "light"
        ? "0 20px 50px rgba(31, 58, 90, 0.14)"
        : "0 24px 54px rgba(0, 0, 0, 0.34)",
    backdropFilter: "blur(16px)",
    border:
      theme.mode === "light"
        ? "1px solid rgba(255,255,255,0.9)"
        : "1px solid rgba(255,255,255,0.08)",
  },
  messagesWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    flexGrow: 1,
    minWidth: 0,
    borderRadius: 28,
    overflow: "hidden",
    background:
      theme.mode === "light"
        ? "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,251,255,0.98) 100%)"
        : "linear-gradient(180deg, rgba(18, 31, 46, 0.96) 0%, rgba(14, 25, 37, 0.98) 100%)",
    boxShadow:
      theme.mode === "light"
        ? "0 24px 56px rgba(35, 64, 96, 0.14)"
        : "0 24px 56px rgba(0, 0, 0, 0.36)",
    border:
      theme.mode === "light"
        ? "1px solid rgba(255,255,255,0.92)"
        : "1px solid rgba(255,255,255,0.07)",
  },
  welcomeMsg: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    textAlign: "center",
    padding: theme.spacing(4),
    background:
      theme.mode === "light"
        ? "radial-gradient(circle at top right, rgba(32, 156, 238, 0.12), transparent 28%), linear-gradient(180deg, rgba(245,250,255,0.92) 0%, rgba(236,244,251,0.96) 100%)"
        : "radial-gradient(circle at top right, rgba(32, 156, 238, 0.12), transparent 28%), linear-gradient(180deg, rgba(21,36,53,0.92) 0%, rgba(16,28,42,0.97) 100%)",
  },
  dragger: {
    width: 5,
    cursor: "ew-resize",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    userSelect: "none",
    background:
      theme.mode === "light"
        ? "linear-gradient(180deg, rgba(16,105,177,0.08) 0%, rgba(16,105,177,0.18) 50%, rgba(16,105,177,0.08) 100%)"
        : "linear-gradient(180deg, rgba(74,167,255,0.10) 0%, rgba(74,167,255,0.24) 50%, rgba(74,167,255,0.10) 100%)",
    "&:hover": {
      background:
        theme.mode === "light"
          ? "linear-gradient(180deg, rgba(16,105,177,0.18) 0%, rgba(16,105,177,0.34) 50%, rgba(16,105,177,0.18) 100%)"
          : "linear-gradient(180deg, rgba(74,167,255,0.18) 0%, rgba(74,167,255,0.34) 50%, rgba(74,167,255,0.18) 100%)",
    },
  },
  logo: {
    width: "42%",
    maxWidth: 220,
    objectFit: "contain",
    content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")",
  },
  welcomeCard: {
    width: "100%",
    maxWidth: 520,
    padding: theme.spacing(5, 4),
    borderRadius: 30,
    background:
      theme.mode === "light"
        ? "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(242,248,253,0.98) 100%)"
        : "linear-gradient(180deg, rgba(24, 40, 58, 0.96) 0%, rgba(17, 28, 42, 0.99) 100%)",
    border:
      theme.mode === "light"
        ? "1px solid rgba(255,255,255,0.95)"
        : "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      theme.mode === "light"
        ? "0 24px 60px rgba(30, 61, 95, 0.12)"
        : "0 24px 60px rgba(0, 0, 0, 0.34)",
  },
  welcomeBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0.75, 1.5),
    marginBottom: theme.spacing(2),
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.mode === "light" ? "#1a4f7a" : "#a7d8ff",
    background: theme.mode === "light" ? "rgba(22, 115, 193, 0.10)" : "rgba(78, 173, 255, 0.12)",
  },
  welcomeLogoWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  welcomeTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    lineHeight: 1.1,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
  },
  welcomeText: {
    fontSize: "1rem",
    lineHeight: 1.7,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  welcomeHintRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  welcomeHint: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 18,
    textAlign: "left",
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    background: theme.mode === "light" ? "rgba(255,255,255,0.84)" : "rgba(255,255,255,0.04)",
    border:
      theme.mode === "light"
        ? "1px solid rgba(31, 71, 111, 0.08)"
        : "1px solid rgba(255,255,255,0.06)",
  },
}));

const TicketsCustom = () => {
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const { ticketId } = useParams();

  const [ticketsManagerWidth, setTicketsManagerWidth] = useState(0);
  const ticketsManagerWidthRef = useRef(ticketsManagerWidth);

  useEffect(() => {
    if (user && user.defaultTicketsManagerWidth) {
      setTicketsManagerWidth(user.defaultTicketsManagerWidth);
    } else {
      setTicketsManagerWidth(defaultTicketsManagerWidth);
    }
  }, [user]);

  const handleSaveContact = async (value) => {
    let nextValue = value;
    if (nextValue < minTicketsManagerWidth) {
      nextValue = minTicketsManagerWidth;
    }
    await api.put(`/users/toggleChangeWidht/${user.id}`, { defaultTicketsManagerWidth: nextValue });
  };

  const handleMouseMove = useCallback((e) => {
    const newWidth = e.clientX - document.body.offsetLeft;
    if (newWidth > minTicketsManagerWidth && newWidth < maxTicketsManagerWidth) {
      ticketsManagerWidthRef.current = newWidth;
      setTicketsManagerWidth(newWidth);
    }
  }, []);

  const handleMouseUp = async () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);

    const newWidth = ticketsManagerWidthRef.current;
    if (newWidth !== ticketsManagerWidth) {
      await handleSaveContact(newWidth);
    }
  };

  const handleMouseDown = () => {
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };

  return (
    <QueueSelectedProvider>
      <div className={classes.chatContainer}>
        <div className={classes.chatPaper}>
          <div className={classes.contactsWrapper} style={{ width: ticketsManagerWidth }}>
            <TicketsManager />
            <div onMouseDown={handleMouseDown} className={classes.dragger} />
          </div>

          <div className={classes.messagesWrapper}>
            {ticketId ? (
              <Ticket />
            ) : (
              <Hidden only={["sm", "xs"]}>
                <Paper square variant="outlined" className={classes.welcomeMsg}>
                  <div className={classes.welcomeCard}>
                    <div className={classes.welcomeBadge}>Centro de conversaciones</div>
                    <div className={classes.welcomeLogoWrap}>
                      <img className={classes.logo} alt="" />
                    </div>
                    <div className={classes.welcomeTitle}>Todo listo para atender mejor</div>
                    <div className={classes.welcomeText}>
                      {i18n.t("chat.noTicketMessage")} Use la columna izquierda para filtrar conversaciones,
                      abrir tickets activos o crear uno nuevo sin salir de esta vista.
                    </div>
                    <div className={classes.welcomeHintRow}>
                      <div className={classes.welcomeHint}>
                        <strong>Busqueda rapida</strong>
                        <br />
                        Encuentre chats por nombre, numero o mensajes.
                      </div>
                      <div className={classes.welcomeHint}>
                        <strong>Flujo mas claro</strong>
                        <br />
                        Revise atendiendo, esperando y grupos desde un mismo panel.
                      </div>
                    </div>
                  </div>
                </Paper>
              </Hidden>
            )}
          </div>
        </div>
      </div>
    </QueueSelectedProvider>
  );
};

export default TicketsCustom;
